import { ThemeEvents } from "@theme/events";

/**
 * Escape PDP price — updates sale/compare/% on variant change via section HTML.
 */
class EscapePdpPrice extends HTMLElement {
  connectedCallback() {
    this.#section = this.closest(".shopify-section");
    this.#section?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate,
    );
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate,
    );
  }

  /** @type {Element | null} */
  #section = null;

  /** @param {CustomEvent} event */
  #onVariantUpdate = (event) => {
    const { data } = event.detail;
    if (
      !data?.html ||
      String(data.productId) !== String(this.dataset.productId)
    )
      return;

    const next = data.html.querySelector(
      `escape-pdp-price[data-block-id="${this.dataset.blockId}"]`,
    );
    if (!next) return;

    const current = this.querySelector('[ref="priceContainer"]');
    const incoming = next.querySelector('[ref="priceContainer"]');
    if (current && incoming) {
      current.replaceWith(incoming);
    }

    parkPayCardAfterVariants();

    const installments = this.querySelector(".escape-pdp-price__installments");
    const nextInstallments = next.querySelector(
      ".escape-pdp-price__installments",
    );
    if (nextInstallments && !installments) {
      this.appendChild(nextInstallments.cloneNode(true));
    } else if (nextInstallments && installments) {
      installments.replaceWith(nextInstallments.cloneNode(true));
    } else {
      installments?.remove();
    }
  };
}

if (!customElements.get("escape-pdp-price")) {
  customElements.define("escape-pdp-price", EscapePdpPrice);
}

/** Size row to hang the guide on — fieldset legend, or one-size plain label. */
function findSizeGuideMountRow() {
  const existing = document.querySelector(
    ".template-product .escape-pdp-size-row",
  );
  if (existing) return existing;

  for (const fieldset of document.querySelectorAll(
    ".template-product .variant-picker fieldset",
  )) {
    const legend = fieldset.querySelector("legend");
    if (!legend || !/\bsize\b/i.test(legend.textContent)) continue;

    const row = document.createElement("div");
    row.className = "escape-pdp-size-row";
    fieldset.insertBefore(row, legend);
    row.appendChild(legend);
    return row;
  }

  // One size: variant picker renders a div, not a fieldset
  for (const el of document.querySelectorAll(
    ".template-product .variant-picker [data-testid='variant-option-single']",
  )) {
    if (!/\bsize\b/i.test(el.textContent)) continue;

    const label = document.createElement("span");
    label.className = "escape-pdp-size-label";
    while (el.firstChild) label.appendChild(el.firstChild);
    el.appendChild(label);
    el.classList.add("escape-pdp-size-row");
    return el;
  }

  return null;
}

/** ponytail: color sits under prepaid banner; pay card was in the way inside the price stack. */
function parkPayCardAfterVariants() {
  const picker = document.querySelector(
    ".template-product .product-details variant-picker",
  );
  const cards = document.querySelectorAll(
    ".template-product .escape-pdp-pay-card",
  );
  if (!picker || !cards.length) return;
  const card = cards[cards.length - 1];
  for (const el of cards) {
    if (el !== card) el.remove();
  }
  picker.after(card);
}

/** Mount size guide trigger beside the Size variant label. */
function mountSizeGuide() {
  const chart = document.querySelector(".template-product .escape-size-chart");
  if (!chart) return;

  // Keep the source trigger in the chart; clone into the size row so morph
  // of the variant picker can't destroy the only copy.
  const sourceTrigger = chart.querySelector(
    ":scope > .escape-size-chart__trigger",
  );
  if (!sourceTrigger) return;

  const liveSlot = document.querySelector(
    ".template-product .escape-pdp-size-guide-slot",
  );
  if (liveSlot?.querySelector(".escape-size-chart__trigger")) {
    chart.classList.add("escape-size-chart--inline");
    return;
  }

  liveSlot?.remove();

  const row = findSizeGuideMountRow();
  if (!row) return;

  const slot = document.createElement("div");
  slot.className = "escape-pdp-size-guide-slot";
  slot.appendChild(sourceTrigger.cloneNode(true));
  row.appendChild(slot);

  chart.classList.add("escape-size-chart--inline");
}

function initCouponCopy() {
  document.addEventListener("click", async (event) => {
    const btn = event.target.closest("[data-escape-copy-code]");
    if (!btn) return;

    const code = btn.getAttribute("data-escape-copy-code");
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
      const prev = btn.textContent;
      btn.textContent = "Copied";
      btn.classList.add("is-copied");
      window.setTimeout(() => {
        btn.textContent = prev;
        btn.classList.remove("is-copied");
      }, 1600);
    } catch {
      // ponytail: clipboard denied — leave label alone
    }
  });
}

/** @type {string} */
let lastPdpColor = "";

function readSelectedColor() {
  const picker = document.querySelector(".template-product variant-picker");
  if (!picker) return "";
  for (const input of picker.querySelectorAll("input:checked")) {
    const name = (input.getAttribute("name") || "").toLowerCase();
    if (name.startsWith("color") || name.startsWith("colour")) {
      return input.value.trim();
    }
  }
  return "";
}

function scrollGalleryOnMobileColorChange() {
  const color = readSelectedColor();
  if (!color || color === lastPdpColor) {
    lastPdpColor = color || lastPdpColor;
    return;
  }
  lastPdpColor = color;
  if (!window.matchMedia("(max-width: 749px)").matches) return;

  const gallery = document.querySelector(".template-product media-gallery");
  gallery?.scrollIntoView({
    behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? "auto"
      : "smooth",
    block: "start",
  });
}

const SIZE_OPTION_RE = /\bsize\b/i;
/** @type {boolean} */
let sizePicked = new URLSearchParams(location.search).has("variant");
/** @type {"atc" | "sticky" | "buynow" | null} */
let pendingAfterSize = null;

function sizeFieldsets() {
  return [
    ...document.querySelectorAll(".template-product .variant-picker fieldset"),
  ].filter((fs) =>
    SIZE_OPTION_RE.test(fs.querySelector("legend")?.textContent || ""),
  );
}

function requiresSizePick() {
  return sizeFieldsets().some(
    (fs) => fs.querySelectorAll('input[type="radio"]').length > 1,
  );
}

function sizeIsSelected() {
  return sizeFieldsets().some((fs) => fs.querySelector("input:checked"));
}

function clearCheckedSize(input) {
  input.checked = false;
  input.removeAttribute("checked");
  input.dataset.currentChecked = "false";
}

function uncheckSizeIfNeeded() {
  if (!requiresSizePick()) return;
  for (const fs of sizeFieldsets()) {
    for (const input of fs.querySelectorAll("input:checked")) {
      if (input.dataset.variantAvailable === "false") {
        clearCheckedSize(input);
        sizePicked = false;
      }
    }
  }
  if (sizePicked && !sizeIsSelected()) sizePicked = false;
  if (sizePicked) return;
  for (const fs of sizeFieldsets()) {
    for (const input of fs.querySelectorAll("input:checked")) {
      clearCheckedSize(input);
    }
  }
}

function applySizeGate() {
  if (!document.querySelector(".template-product")) return;
  uncheckSizeIfNeeded();
  syncBuyNowAvailability();
}

/** @returns {HTMLDialogElement} */
function ensureSizePickDialog() {
  let dialog = document.querySelector("dialog.escape-size-pick");
  if (dialog instanceof HTMLDialogElement) return dialog;

  dialog = document.createElement("dialog");
  dialog.className = "escape-size-pick";
  dialog.innerHTML =
    '<div class="escape-size-pick__inner">' +
    '<div class="escape-size-pick__header">' +
    '<h2 class="escape-size-pick__title">Select size</h2>' +
    '<div class="escape-pdp-size-guide-slot"></div>' +
    '<button type="button" class="escape-size-pick__close" aria-label="Close">×</button>' +
    "</div>" +
    '<div class="escape-size-pick__options"></div>' +
    "</div>";
  document.body.appendChild(dialog);

  dialog
    .querySelector(".escape-size-pick__close")
    ?.addEventListener("click", () => dialog.close());
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close();
  });
  dialog.addEventListener("close", () => {
    if (!sizePicked) pendingAfterSize = null;
  });
  return dialog;
}

function mountSizeGuideInPick(dialog) {
  const slot = dialog.querySelector(
    ".escape-size-pick__header .escape-pdp-size-guide-slot",
  );
  if (!slot || slot.querySelector(".escape-size-chart__trigger")) return;
  const source = document.querySelector(
    ".template-product .escape-size-chart > .escape-size-chart__trigger",
  );
  if (!source) return;
  slot.appendChild(source.cloneNode(true));
}

function openSizePick() {
  const fs = sizeFieldsets()[0];
  if (!fs) return;

  const dialog = ensureSizePickDialog();
  mountSizeGuideInPick(dialog);
  const box = dialog.querySelector(".escape-size-pick__options");
  if (!box) return;
  box.replaceChildren();

  for (const input of fs.querySelectorAll('input[type="radio"]')) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "escape-size-pick__opt";
    btn.textContent = (
      input
        .closest("label")
        ?.querySelector("[data-key='variant-option-text']")
        ?.textContent || input.value
    ).trim();
    if (
      input.dataset.optionAvailable === "false" ||
      input.dataset.variantAvailable === "false"
    ) {
      btn.disabled = true;
      btn.classList.add("is-unavailable");
    }
    btn.addEventListener("click", () => {
      sizePicked = true;
      const waited = pendingAfterSize;
      input.click();
      dialog.close();
      // ponytail: same size as the hidden variant id may skip morph
      window.setTimeout(() => {
        if (pendingAfterSize === waited) resumeAfterSize();
      }, 600);
    });
    box.appendChild(btn);
  }

  dialog.showModal();
}

window.escapeBeforeGokwikBuyNow = function escapeBeforeGokwikBuyNow() {
  if (!requiresSizePick() || sizePicked || sizeIsSelected()) return false;
  pendingAfterSize = "buynow";
  openSizePick();
  return true;
};

function resumeAfterSize() {
  const action = pendingAfterSize;
  pendingAfterSize = null;
  if (!action) return;

  if (action === "buynow") {
    document.querySelector("#gokwik-buy-now")?.click();
    return;
  }
  if (action === "sticky") {
    document
      .querySelector(".template-product .sticky-add-to-cart__button")
      ?.click();
    return;
  }
  document
    .querySelector(
      ".template-product .product-form-buttons .add-to-cart-button",
    )
    ?.click();
}

function initPdpEnhancements() {
  const root = document.querySelector(".template-product");
  const section = root?.querySelector(".product-information");
  if (!section) return;

  mountSizeGuide();
  parkPayCardAfterVariants();
  applySizeGate();
  lastPdpColor = readSelectedColor();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initPdpEnhancements);
} else {
  initPdpEnhancements();
}

initCouponCopy();

document.addEventListener(
  "click",
  (event) => {
    if (event.target.closest("dialog.escape-size-pick")) return;
    const atc = event.target.closest(
      ".template-product .product-form-buttons .add-to-cart-button",
    );
    const sticky = event.target.closest(
      ".template-product .sticky-add-to-cart__button",
    );
    const buyNow = event.target.closest("[data-escape-buy-now]");
    if (!atc && !sticky && !buyNow) return;
    if (!requiresSizePick() || sizePicked || sizeIsSelected()) return;
    window.__escapeGkAllow = false;
    document.documentElement.removeAttribute("data-escape-gk-open");
    event.preventDefault();
    event.stopImmediatePropagation();
    pendingAfterSize = buyNow ? "buynow" : sticky ? "sticky" : "atc";
    openSizePick();
  },
  true,
);

document.addEventListener(
  "submit",
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.closest(".template-product")) return;
    if (!requiresSizePick() || sizePicked) return;
    window.__escapeGkAllow = false;
    document.documentElement.removeAttribute("data-escape-gk-open");
    event.preventDefault();
    event.stopImmediatePropagation();
    pendingAfterSize = "atc";
    openSizePick();
  },
  true,
);

document.addEventListener("click", (event) => {
  const seeHow = event.target.closest("[data-escape-see-how]");
  if (seeHow) {
    const card = document.querySelector(".template-product .escape-pdp-pay-card");
    if (!card) return;
    event.preventDefault();
    card.open = true;
    card.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
      block: "center",
    });
    return;
  }

  const reviewsLink = event.target.closest("[data-escape-reviews]");
  if (reviewsLink) {
    const target = document.querySelector(
      "#judgeme_product_reviews, .jdgm-rev-widg, #shopify-section-judgeme_reviews",
    );
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
      block: "start",
    });
    return;
  }
});

document.addEventListener("change", (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement)) return;
  if (!input.closest(".template-product variant-picker")) return;
  const legend = input.closest("fieldset")?.querySelector("legend");
  if (SIZE_OPTION_RE.test(legend?.textContent || "") && input.checked) {
    sizePicked = input.dataset.variantAvailable !== "false";
  }
  applySizeGate();
});

document.addEventListener(ThemeEvents.variantUpdate, () => {
  mountSizeGuide();
  parkPayCardAfterVariants();
  applySizeGate();
  scrollGalleryOnMobileColorChange();
  resumeAfterSize();
});

/** Enable/disable Buy now when variant availability changes. */
function syncBuyNowAvailability(scope) {
  const root = scope || document;
  const buttons = root.querySelectorAll("[data-escape-buy-now]");
  if (!buttons.length) return;
  const atc = root.querySelector(
    ".template-product .product-form-buttons .add-to-cart-button, product-form-component .add-to-cart-button",
  );
  const available = atc ? !atc.disabled : true;
  for (const btn of buttons) {
    btn.disabled = !available;
    btn.classList.toggle("disabled", !available);
  }
}
